import React from 'react';
import '../../../styles/mastercertificate.css';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';

interface AddNewSignatureProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

function dataUrlToBytes(dataUrl: string): number[] {
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  const binary = atob(base64);
  const bytes: number[] = [];
  for (let i = 0; i < binary.length; i++) bytes.push(binary.charCodeAt(i));
  return bytes;
}

async function textToDataUrl(text: string, fontFamily: string): Promise<string> {
  // Wait for the font to be available before drawing to canvas.
  // Without this, canvas falls back to the browser default font.
  try { await document.fonts.load(`52px "${fontFamily}"`); } catch {}
  const canvas = document.createElement('canvas');
  canvas.width = 520; canvas.height = 120;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = `52px "${fontFamily}"`;
  ctx.fillStyle = '#1f2937';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 20, 62);
  return canvas.toDataURL('image/png');
}

// Map NowCerts Windows system font names → loaded Google Fonts equivalents.
// The browser cannot access local system fonts, so we substitute visually similar web fonts.
const NC_FONT_TO_WEB: Record<string, string> = {
  'Blackadder ITC':      'MedievalSharp',
  'Vladimir Script':     'Alex Brush',
  'Edwardian Script ITC':'Pinyon Script',
  'Freestyle Script':    'Dancing Script',
  'Mistral':             'Pacifico',
  'Pristina':            'Satisfy',
  'Segoe Script':        'Kalam',
};

const AddNewSignature: React.FC<AddNewSignatureProps> = ({ open, onClose, onSaved }) => {
  const [name, setName] = React.useState('');
  const [activeTab, setActiveTab] = React.useState(0);
  const [textSignature, setTextSignature] = React.useState('');
  const [textStyle, setTextStyle] = React.useState('');
  const [styleOptions, setStyleOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = React.useState('');
  const [drawDataUrl, setDrawDataUrl] = React.useState<string>('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);

  // Load signature styles from API
  React.useEffect(() => {
    if (!open) return;
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-styles`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: { key: string; value: string }[]) => {
        if (Array.isArray(data) && data.length > 0) {
          setStyleOptions(data.map(s => ({ label: s.value, value: s.key })));
          setTextStyle(prev => prev || data[0].key);
        }
      })
      .catch(() => {});
  }, [open]);

  const reset = () => {
    setName(''); setActiveTab(0); setTextSignature('');
    setUploadedFile(null); setUploadPreview(''); setDrawDataUrl(''); setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleCanvasPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) { ctx.beginPath(); const rect = canvas.getBoundingClientRect(); ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top); }
    }
  };
  const handleCanvasPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) { const rect = canvas.getBoundingClientRect(); ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top); ctx.stroke(); }
    }
  };
  const handleCanvasPointerUp = () => {
    setIsDrawing(false);
    if (canvasRef.current) setDrawDataUrl(canvasRef.current.toDataURL());
  };
  const handleClearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) { canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height); setDrawDataUrl(''); }
  };

  const ncFontName = styleOptions.find(s => s.value === textStyle)?.label ?? '';
  const currentFontFamily = NC_FONT_TO_WEB[ncFontName] ?? (ncFontName || 'cursive');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      const reader = new FileReader();
      reader.onload = ev => setUploadPreview(ev.target?.result as string || '');
      reader.readAsDataURL(file);
    }
  };

  const handleCreate = async () => {
    setError('');
    if (!name.trim()) { setError('Signature name is required.'); return; }

    let signatureBytes: number[] = [];

    if (activeTab === 0) {
      if (!textSignature.trim()) { setError('Please enter signature text.'); return; }
      signatureBytes = dataUrlToBytes(await textToDataUrl(textSignature, currentFontFamily));
    } else if (activeTab === 1) {
      if (!uploadPreview) { setError('Please upload a signature image.'); return; }
      signatureBytes = dataUrlToBytes(uploadPreview);
    } else {
      if (!drawDataUrl) { setError('Please draw a signature.'); return; }
      signatureBytes = dataUrlToBytes(drawDataUrl);
    }

    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signatures`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
          'Content-Type': 'application/json',
          NCToken: localStorage.getItem('NCToken') || '',
        },
        body: JSON.stringify({ signatureId: '', signatureBytes, signatureName: name }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      reset();
      onSaved?.();
      onClose();
    } catch {
      setError('Failed to save signature. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="masterCert__popupOverlay">
      <div className="masterCert__popup">
        <div className="masterCert__popupHeader">
          <span>Add New Signature</span>
          <button className="masterCert__popupCloseBtn" onClick={handleClose}>×</button>
        </div>
        <div className="carrierDialog__body">
          <div className="carrierDialog__field">
            <label className="carrierDialog__label">Signature Name <span style={{ color: '#dc2626' }}>*</span></label>
            <InputText maxLength={100}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. John Smith - Agent Signature"
              className="carrierDialog__input"
            />
          </div>
          <div className="carrierDialog__field">
            <TabView activeIndex={activeTab} onTabChange={e => setActiveTab(e.index)}>
              <TabPanel header="Text Signature">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 8 }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label className="carrierDialog__label">Text</label>
                      <InputText maxLength={500} className="carrierDialog__input" value={textSignature} onChange={e => setTextSignature(e.target.value)} placeholder="Enter signature text" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="carrierDialog__label">Style</label>
                      <Dropdown
                        className="carrierDialog__input"
                        value={textStyle}
                        options={styleOptions}
                        optionLabel="label"
                        optionValue="value"
                        onChange={e => setTextStyle(e.value)}
                        placeholder="Select style"
                        emptyMessage="No available options"
                      />
                    </div>
                  </div>
                  {textSignature && (
                    <div style={{ padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb', fontSize: 28, fontFamily: `"${currentFontFamily}", cursive`, color: '#1f2937', minHeight: 56 }}>
                      {textSignature}
                    </div>
                  )}
                </div>
              </TabPanel>
              <TabPanel header="Upload Signature">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 8 }}>
                  <input type="file" accept="image/*" onChange={handleFileChange} id="sig-upload-input" style={{ display: 'none' }} />
                  <label htmlFor="sig-upload-input" className="carrierDialog__uploadDottedBox">
                    {uploadPreview
                      ? <img src={uploadPreview} alt="preview" style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }} />
                      : uploadedFile ? uploadedFile.name : 'Drag & drop or click to upload'}
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="button" label="Upload" className="carrierDialog__secondaryBtn" onClick={() => document.getElementById('sig-upload-input')?.click()} />
                    {uploadedFile && <Button type="button" label="Remove" className="p-button-text carrierDialog__cancelBtn" onClick={() => { setUploadedFile(null); setUploadPreview(''); }} />}
                  </div>
                </div>
              </TabPanel>
              <TabPanel header="Draw Signature">
                <div className="carrierDialog__drawAreaAligned">
                  <canvas
                    ref={canvasRef}
                    width={450}
                    height={180}
                    className="carrierDialog__drawCanvas"
                    style={{ border: '1.5px solid #0057A4', borderRadius: 8, background: '#fff', width: '100%', maxWidth: 450, height: 180, cursor: 'crosshair' }}
                    onPointerDown={handleCanvasPointerDown}
                    onPointerMove={handleCanvasPointerMove}
                    onPointerUp={handleCanvasPointerUp}
                    onPointerCancel={handleCanvasPointerUp}
                  />
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                    <Button type="button" label="Clear" className="carrierDialog__secondaryBtn" onClick={handleClearCanvas} />
                  </div>
                </div>
              </TabPanel>
            </TabView>
          </div>
          {error && (
            <div style={{ color: '#dc2626', fontSize: 13, padding: '6px 10px', background: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca' }}>
              {error}
            </div>
          )}
        </div>
        <div className="carrierDialog__footer">
          <Button type="button" label="Cancel" className="p-button-text carrierDialog__cancelBtn" onClick={handleClose} disabled={saving} />
          <Button
            type="button"
            label={saving ? 'Saving…' : 'Create'}
            icon={saving ? 'pi pi-spin pi-spinner' : undefined}
            className="p-button-primary carrierDialog__saveBtn"
            onClick={handleCreate}
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
};

export default AddNewSignature;
